/**
 * Fetches auth-on-file data from Postgres and transforms it into the
 * side-navigation modal payload used by {@link DocViewerWithSideNavigation}.
 *
 * @module generateDataForDocViewerWithSideNavFromAuthOnFile
 */
import { singleOrderDetailsGenericCallForPostgres } from "../../../../api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import { AuthOnFile } from "../../../../data-model/medicalPaOrdersModel";
import { SingleOrderDetailsTableName } from "../../../../enums/singleOrderDetailsTableName";
import { generateDataForDocWithSideNavModalFromAuthOnFile } from "../../../../utils/createDataForDocWithSideNavModal";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";

/** Shape of the Postgres response for the auth-on-file endpoint. */
interface AuthOnFileDetailsResponse {
  auth_on_file: AuthOnFile | null;
}

/**
 * Fetches auth-on-file data for a given order and transforms it into
 * the side-navigation modal payload.
 *
 * @param id - The order ID to fetch auth-on-file data for
 * @param signal - Optional AbortSignal to cancel the in-flight request
 * @returns Array of nav items with J-code documents, or empty array on failure
 */
export const generateDataForDocViewerWithSideNavFromAuthOnFile = async (
  id?: string,
  signal?: AbortSignal,
) => {
  if (!id?.trim()) return [];

  try {
    const response =
      await singleOrderDetailsGenericCallForPostgres<AuthOnFileDetailsResponse>(
        {
          order_id: id,
          org_id: getOrgIdForFetchExternalWorklist(),
          table_name: SingleOrderDetailsTableName.AUTH_ON_FILE,
        },
        signal,
      );
    return (
      generateDataForDocWithSideNavModalFromAuthOnFile(
        response.auth_on_file ?? undefined,
      ) ?? []
    );
  } catch (error) {
    console.error(
      "Error generating data for doc viewer with side nav from auth on file:",
      error,
    );
    return [];
  }
};
